"use client";

import { useQuery } from "@apollo/client/react";
import type { Route } from "next";
import { usePathname, useRouter } from "next/navigation";
import { createContext, type ReactNode, useContext, useEffect } from "react";
import { graphql } from "@/lib/@generated/gql";
import type { MeQuery } from "@/lib/@generated/graphql";
import { isUserQueryAuthenticationError } from "@/lib/errors/authErrors";
import { clearUserContext, setUserContext } from "@/lib/sentry/error-tracking";
import { clearAllTokens, isSuperAdmin } from "@/lib/tokenManager";
import { useAuth } from "@/providers/AuthProvider";

const ME_QUERY = graphql(`
  query Me {
    me {
      id
      profile {
        fullName
        preferredTemperatureUnit
        preferredTimeZone
        profilePhoto
      }
      email {
        email
        verified
      }
      memberships {
        id
        role
        isAdmin
        organization {
          id
          name
        }
      }
    }
  }
`);

// Extract types from the generated query type
type User = NonNullable<MeQuery["me"]>;

interface UserContextType {
  user: User | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export function UserProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading: authLoading, accessToken } = useAuth();
  const router = useRouter();
  const pathname = usePathname();

  const shouldSkipQuery = authLoading || !isAuthenticated || !accessToken;

  const {
    data,
    loading,
    error: queryError,
    refetch,
  } = useQuery(ME_QUERY, {
    fetchPolicy: "cache-first",
    skip: shouldSkipQuery,
  });

  const handleRefetch = async () => {
    if (shouldSkipQuery) {
      return;
    }
    await refetch();
  };

  // Track user context in Sentry for error reporting
  useEffect(() => {
    if (data?.me) {
      const primaryMembership = data.me.memberships?.[0];
      setUserContext({
        id: data.me.id,
        email: data.me.email?.email,
        username: data.me.profile?.fullName || undefined,
        organizationId: primaryMembership?.organization?.id,
        organizationName: primaryMembership?.organization?.name || undefined,
      });
    } else if (!isAuthenticated) {
      clearUserContext();
    }
  }, [data, isAuthenticated]);
  // Create context value with current state
  const value: UserContextType = {
    user: data?.me ?? null,
    loading: authLoading || loading,
    error: !shouldSkipQuery && queryError ? (queryError as Error) : null,
    refetch: handleRefetch,
  };

  // Check if error is an authentication error (even with cached data)
  const isAuthError = isUserQueryAuthenticationError(queryError);

  // Check if authenticated user is a super admin
  // The admin app is only accessible to super admins
  const hasSuperAdminAccess = isSuperAdmin(accessToken);

  // Handle auth errors and super admin access check via useEffect to avoid side effects during render
  useEffect(() => {
    if (authLoading) {
      return;
    }

    // Skip redirects if already on auth-related pages to prevent loops
    const isOnAuthPage =
      pathname === "/signin" ||
      pathname === "/unauthorized" ||
      pathname === "/auth/callback" ||
      pathname === "/auth/switch-callback" ||
      pathname === "/switch-organizations";

    if (isOnAuthPage) {
      return;
    }

    // If auth error detected, clear tokens and redirect to sign-in
    if (isAuthError) {
      clearAllTokens();
      router.push("/signin");
      return;
    }

    // If authenticated but not a super admin, redirect to unauthorized page
    // Do NOT clear tokens - the user is still logged in, just not authorized for admin
    // This allows them to switch to super admin or log out from the unauthorized page
    if (isAuthenticated && !hasSuperAdminAccess) {
      router.push("/unauthorized" as Route);
    }
  }, [authLoading, isAuthError, isAuthenticated, hasSuperAdminAccess, router, pathname]);

  // CRITICAL: Always render children - never block with EmptyState
  // Components that need user data should check the loading/error state themselves
  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}
